import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faBook,
  faBullseye,
  faChalkboardUser,
  faCheck,
  faClipboardCheck,
  faCode,
  faCodeBranch,
  faCrosshairs,
  faHammer,
  faHandshake,
  faLockOpen,
  faRobot,
  faRocket,
  faShieldHalved,
  faTag,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import { faEnvelope } from '@fortawesome/free-regular-svg-icons';

import useConfig from '../useConfig';
import { AdHocAdvice } from '../Components/Minnect';
import { CASE_STUDIES, CONTACT_EMAIL, contactHref, formatUsd, Overline, PrimaryButton, OutlineButton } from '../Components/ServicePage';


// Sprints are sold on a fixed price, not hours. Change a price in one place:
// the sprint's `pricing` below. Bespoke work is the one hourly option, at
// BESPOKE_RATE.

// "How sprints work": what happens inside one sprint.
const STEPS = [
  { icon: faBullseye, title: 'Define', body: 'We identify the problem, establish the scope, and agree on what "done" looks like.' },
  { icon: faHammer, title: 'Build', body: 'Code Scribes handles the engineering work required to deliver the agreed solution.' },
  { icon: faClipboardCheck, title: 'Validate', body: 'We test the implementation against the defined requirements and make sure it is ready for use.' },
  { icon: faHandshake, title: 'Handoff', body: 'You receive the source code, documentation, infrastructure, and knowledge required to operate and continue developing the solution.' }
];

// `pricing.options` is one price, or one per format (onsite, virtual).
// `pricing.length` is how long the sprint takes, as shown on the page.
const SPRINTS = [
  {
    id: 'ai-workshop',
    icon: faChalkboardUser,
    name: 'AI Training Workshop',
    description: 'A hands-on workshop that shows your team practical ways to put AI to work in day-to-day business operations.',
    qualifier: "Not another chatbot demo. Real work, handed off to AI.",
    goodFor: [
      'Leadership teams exploring AI',
      'Operations and back-office teams',
      'Finding automation opportunities',
      'Agentic workflows in practice',
      'Connecting AI to your existing tools',
      'Using AI securely and responsibly'
    ],
    outcomes: [],
    deliverables: [
      'Live workshop, onsite or virtual',
      'Demonstrations built around your operations',
      'Hands-on exercises',
      'Workshop materials',
      'AI opportunities identified in your business',
      'Recommended next steps'
    ],
    pricing: {
      options: [
        { label: 'Onsite', price: 5000 },
        { label: 'Virtual', price: 2500 }
      ],
      length: '1 day',
      title: 'Practical AI for Operations',
      body: 'See where AI fits in the way your business already runs, and leave with a team that knows how to use it.'
    }
  },
  {
    id: 'chaoss',
    icon: faLockOpen,
    name: 'ChaOSS Sprint',
    description: 'Replace a vendor-locked managed service with open source software. Cut infrastructure and licensing costs, and run your application on any cloud.',
    goodFor: [
      'AWS Cognito to Keycloak',
      'Proprietary monitoring to Grafana',
      'Reducing licensing costs',
      'Reducing cloud spend',
      'Avoiding vendor lock-in',
      'Cloud portability',
      'Multi-cloud deployments'
    ],
    outcomes: [
      { title: 'Authentication You Own', body: 'Move users, roles, and sign-in flows from AWS Cognito to a self-hosted Keycloak.' },
      { title: 'Observability You Own', body: 'Replace a proprietary monitoring service with Grafana dashboards and alerts running in your own infrastructure.' }
    ],
    deliverables: [
      'Target architecture',
      'Production-ready open source deployment',
      'Data migration',
      'Application integration changes',
      'Infrastructure as Code',
      'Cutover plan',
      'Documentation',
      'Operational handoff'
    ],
    pricing: {
      options: [{ price: 7500, unit: 'per tool' }],
      length: '2-4 weeks',
      title: 'One Tool, Migrated',
      body: 'Each sprint moves you onto one open source tool. Migrating to both Keycloak and Grafana is two sprints, $15,000 in all.'
    }
  },
  {
    id: 'zero-to-one',
    icon: faRocket,
    name: '0 to 1 MVP Sprint',
    description: 'Turn an idea into a working software product without building an entire engineering organization first.',
    // Keeps "MVP" from being read as "build my entire startup".
    qualifier: "The goal isn't to build everything. It's to build enough to prove the idea.",
    goodFor: [
      'New products',
      'SaaS applications',
      'Internal tools',
      'Proofs of concept',
      'Technical prototypes',
      'New product experiments',
      'Validating a product idea',
      'Demos for customers or investors'
    ],
    outcomes: [],
    deliverablesNote: 'Depending on scope:',
    deliverables: [
      'Technical architecture',
      'Application code',
      'Frontend',
      'Backend & API',
      'Database',
      'Authentication',
      'Cloud infrastructure',
      'CI/CD',
      'Automated tests',
      'Deployment',
      'Technical documentation'
    ],
    pricing: {
      options: [{ price: 10000 }],
      length: '4 weeks',
      title: 'Product Prototype',
      body: 'Take a defined product concept from idea to deployed, working software that can be demonstrated to users, customers, investors, or internal stakeholders.'
    }
  },
  {
    id: 'ai-automation',
    icon: faRobot,
    name: 'AI Automation Sprint',
    description: "Build practical AI capabilities that connect models to your organization's tools, data, and business processes.",
    goodFor: [
      'Agentic workflows',
      'AI agents',
      'MCP servers',
      'Tool integrations',
      'Internal AI assistants',
      'AI-powered business processes',
      'Connecting LLMs to existing APIs and systems',
      'Automating repetitive knowledge work'
    ],
    // Real client work rather than hypotheticals, so the cards render as
    // case studies.
    outcomes: CASE_STUDIES,
    deliverables: [
      'Working implementation',
      'Source code',
      'Agent and tool definitions',
      'API integrations',
      'Automated tests',
      'Deployment configuration',
      'Technical documentation',
      'Handoff session',
      'Infrastructure required to run the system'
    ],
    pricing: {
      options: [{ price: 15000 }],
      length: '6-8 weeks',
      title: 'Agentic Workflow Build',
      body: 'Turn a manual business process into an AI-powered workflow that can reason, use tools, and execute defined tasks.'
    }
  }
];

// For work that doesn't fit a sprint. Billed hourly, unlike everything above.
const BESPOKE_RATE = 225;

const BESPOKE_FITS = [
  'Scope that changes as you learn',
  'Ongoing development alongside your team',
  'Problems that need investigating before they can be defined',
  'Work that spans several systems or teams'
];

// What separates a sprint from a freelancer or a traditional consultant.
const INCLUDED = [
  { icon: faCrosshairs, title: 'Defined Scope', body: 'We agree on exactly what the sprint will accomplish before work begins.' },
  { icon: faTag, title: 'Fixed Price', body: 'You know the cost before the sprint starts.' },
  { icon: faCode, title: 'Dedicated Engineering', body: 'The engagement is focused on building the agreed solution, not generating recommendations for someone else to implement.' },
  { icon: faShieldHalved, title: 'Production-Minded Engineering', body: 'Solutions are built with testing, deployment, maintainability, security, and operational considerations appropriate to the project.' },
  { icon: faBook, title: 'Documentation', body: "The solution doesn't disappear into a black box when the engagement ends." },
  { icon: faChalkboardUser, title: 'Knowledge Transfer', body: 'Your team gets the context and knowledge required to operate and extend what was built.' },
  { icon: faCodeBranch, title: 'Source Code & Ownership', body: 'The resulting implementation is handed over to you.' }
];

const NOT_A_SPRINT = [
  { title: 'Hourly staff augmentation', body: "You're not buying 160 hours of an engineer." },
  { title: 'Open-ended consulting', body: "There isn't an indefinite engagement where the scope evolves forever." },
  { title: 'Strategy without execution', body: "You don't receive a roadmap and then have to find someone else to build it." },
  { title: 'A promise to build your entire product', body: 'A sprint has a deliberately constrained objective.' }
];



// Keep these to agentic systems that ran in front of real customers -- the
// point of the section is "real, not a demo" -- and to work that actually used
// AI.
const CASE_STUDY = [
  'Designed a unified operations platform and shipped 8 agentic workflows that onboard clients end to end',
  'Delivered AI agents and MCP servers wiring LLMs into the APIs and business systems of 12 clients',
  ...CASE_STUDIES.filter(({ id }) => id !== 'agentic-workflow').map(({ summary }) => summary)
];

const AI_STACK = ['Claude Code', 'Claude API & SDK', 'AWS Bedrock', 'AWS Quick', 'Ollama', 'MCP'];


const stepNumber = (i) => String(i + 1).padStart(2, '0');


const Hero = () => {
  return (
    <section className='py-16 md:py-24'>
      <Overline>Bring us a problem. We'll build the solution.</Overline>
      <h1 className='font-heading text-5xl leading-none tracking-tight text-neutral-50 sm:text-6xl'>
        Engineering Sprints
      </h1>
      <p className='mt-6 max-w-3xl font-heading text-3xl leading-tight tracking-tight text-neutral-300'>
        Fixed-duration, fixed-price engineering engagements for <span className='text-primary-light'>building, modernizing, and shipping technology.</span>
      </p>
      <p className='mt-6 max-w-[60ch] font-body text-lg leading-relaxed text-neutral-400'>
        Sprints aren't billed by the hour or left open-ended. Each one starts with a defined objective and ends
        with a tangible technical outcome.
      </p>
      <div className='mt-8 flex flex-col gap-3 sm:flex-row'>
        <PrimaryButton href={contactHref('Start a Sprint')}>Start a Sprint</PrimaryButton>
        <OutlineButton href={contactHref('Discuss a Project')}>Discuss a Project</OutlineButton>
      </div>
    </section>
  );
};


const HowItWorks = () => {
  return (
    <section className='border-t border-neutral-800 py-16'>
      <Overline>How sprints work</Overline>
      <h2 className='max-w-3xl font-heading text-4xl tracking-tight text-neutral-50'>
        A defined problem. A defined timeline. A defined outcome.
      </h2>
      <p className='mt-4 max-w-[60ch] font-body text-neutral-400'>
        A sprint isn't a block of engineering hours. It's an agreed result, built, tested, and handed over.
      </p>
      <ol className='mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4'>
        {STEPS.map(({ icon, title, body }, i) => (
          <li key={title} className='rounded-lg border border-neutral-800 bg-neutral-900 p-6'>
            <div className='flex items-center justify-between'>
              <span className='font-mono text-sm text-primary-light'>{stepNumber(i)}</span>
              <FontAwesomeIcon icon={icon} className='text-primary-light' size='lg' />
            </div>
            <h3 className='mt-4 font-heading text-2xl text-neutral-50'>{title}</h3>
            <p className='mt-2 font-body text-sm leading-relaxed text-neutral-400'>{body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
};


const CheckList = ({ items, className }) => {
  return (
    <ul className={classNames('flex flex-col gap-3', className)}>
      {items.map((item) => (
        <li key={item} className='flex items-start gap-3 font-body text-sm text-neutral-300'>
          <FontAwesomeIcon icon={faCheck} className='mt-1 flex-shrink-0 text-primary-light' size='xs' />
          {item}
        </li>
      ))}
    </ul>
  );
};


const SprintCard = ({ sprint, index }) => {
  const { id, icon, name, description, qualifier, goodFor, outcomes, deliverablesNote, deliverables, pricing } = sprint;

  return (
    <article id={id} className='scroll-mt-20 grid grid-cols-1 gap-10 rounded-lg border border-neutral-800 bg-neutral-900 p-6 sm:p-10 lg:grid-cols-12'>
      <div className='lg:col-span-7'>
        <div className='flex items-center gap-3'>
          <span className='flex h-10 w-10 items-center justify-center rounded-lg bg-primary-light/10'>
            <FontAwesomeIcon icon={icon} className='text-primary-light' />
          </span>
          <span className='font-body text-xs uppercase tracking-widest text-neutral-500'>Sprint {stepNumber(index)}</span>
        </div>
        <h3 className='mt-4 font-heading text-4xl tracking-tight text-neutral-50'>{name}</h3>
        <p className='mt-3 max-w-[60ch] font-body text-lg text-neutral-400'>{description}</p>

        {qualifier && (
          <p className='mt-6 border-l-2 border-primary-light pl-4 font-heading text-2xl leading-snug text-neutral-50'>
            {qualifier}
          </p>
        )}

        <h4 className='mt-8 font-body text-xs uppercase tracking-widest text-neutral-500'>Good for</h4>
        <ul className='mt-3 flex flex-wrap gap-2'>
          {goodFor.map((item) => (
            <li key={item} className='rounded-full border border-neutral-800 bg-neutral-950 px-3 py-1 font-mono text-xs text-neutral-300'>
              {item}
            </li>
          ))}
        </ul>

        {outcomes.length > 0 && outcomes[0].outcome && (
          <>
            <h4 className='mt-8 font-body text-xs uppercase tracking-widest text-neutral-500'>Case studies</h4>
            <ul className='mt-3 flex flex-col gap-4'>
              {outcomes.map(({ title, body, outcome }) => (
                <li key={title} className='rounded-lg bg-neutral-800 p-5'>
                  <h5 className='font-heading text-xl text-neutral-50'>{title}</h5>
                  <p className='mt-2 font-body text-sm leading-relaxed text-neutral-400'>{body}</p>
                  <p className='mt-3 flex items-start gap-2.5 font-body text-sm text-neutral-50'>
                    <FontAwesomeIcon icon={faArrowRight} className='mt-1 flex-shrink-0 text-primary-light' size='xs' />
                    {outcome}
                  </p>
                </li>
              ))}
            </ul>
          </>
        )}

        {outcomes.length > 0 && !outcomes[0].outcome && (
          <>
            <h4 className='mt-8 font-body text-xs uppercase tracking-widest text-neutral-500'>Example outcomes</h4>
            <ul className={classNames('mt-3 grid grid-cols-1 gap-4', outcomes.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3')}>
              {outcomes.map(({ title, body }) => (
                <li key={title} className='rounded-lg bg-neutral-800 p-5'>
                  <h5 className='font-heading text-xl text-neutral-50'>{title}</h5>
                  <p className='mt-2 font-body text-sm leading-relaxed text-neutral-400'>{body}</p>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className='flex flex-col gap-8 lg:col-span-5'>
        {/* The concrete thing someone can buy, so it leads the column. */}
        <div className='rounded-lg border border-primary-light bg-neutral-950 p-6'>
          <p className='font-body text-xs uppercase tracking-widest text-primary-light'>Fixed price</p>
          <ul className='mt-3 flex flex-wrap gap-x-6 gap-y-2'>
            {pricing.options.map(({ label, price, unit }) => (
              <li key={label || price}>
                {label && <span className='block font-body text-xs uppercase tracking-widest text-neutral-500'>{label}</span>}
                <span className='font-mono text-3xl text-neutral-50'>{formatUsd(price)}</span>
                {unit && <span className='font-body text-sm text-neutral-500'> {unit}</span>}
              </li>
            ))}
          </ul>
          {pricing.length && <p className='mt-2 font-body text-sm text-neutral-500'>{pricing.length}</p>}
          <h4 className='mt-4 font-heading text-2xl text-neutral-50'>{pricing.title}</h4>
          <p className='mt-2 font-body text-sm leading-relaxed text-neutral-400'>{pricing.body}</p>
        </div>

        <div>
          <h4 className='font-body text-xs uppercase tracking-widest text-neutral-500'>Deliverables</h4>
          {deliverablesNote && <p className='mt-2 font-body text-sm text-neutral-500'>{deliverablesNote}</p>}
          <CheckList items={deliverables} className='mt-4 sm:grid sm:grid-cols-2 sm:gap-x-6' />
        </div>
      </div>
    </article>
  );
};


const Catalog = () => {
  return (
    <section id='sprints' className='scroll-mt-20 border-t border-neutral-800 py-16'>
      <Overline>Sprint catalog</Overline>
      <h2 className='font-heading text-4xl tracking-tight text-neutral-50'>Four ways to start.</h2>
      <p className='mt-4 max-w-[60ch] font-body text-neutral-400'>
        Each sprint has a fixed price, and we agree on exactly what "done" looks like before work begins. If none
        of them fit, there's a bespoke option further down.
      </p>
      <div className='mt-12 flex flex-col gap-8'>
        {SPRINTS.map((sprint, i) => (
          <SprintCard key={sprint.id} sprint={sprint} index={i} />
        ))}
      </div>
    </section>
  );
};


const Included = () => {
  return (
    <section className='border-t border-neutral-800 py-16'>
      <Overline>What you get</Overline>
      <h2 className='font-heading text-4xl tracking-tight text-neutral-50'>Every Engineering Sprint includes</h2>
      <ul className='mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4'>
        {INCLUDED.map(({ icon, title, body }) => (
          <li key={title} className='rounded-lg border border-neutral-800 bg-neutral-900 p-6 transition-colors duration-150 hover:border-neutral-700'>
            <FontAwesomeIcon icon={icon} className='text-primary-light' size='lg' />
            <h3 className='mt-4 font-heading text-2xl text-neutral-50'>{title}</h3>
            <p className='mt-2 font-body text-sm leading-relaxed text-neutral-400'>{body}</p>
          </li>
        ))}
      </ul>
    </section>
  );
};


const NotASprint = () => {
  return (
    <section className='grid grid-cols-1 gap-12 border-t border-neutral-800 py-16 lg:grid-cols-12'>
      <div className='lg:col-span-5'>
        <Overline>Setting expectations</Overline>
        <h2 className='font-heading text-4xl tracking-tight text-neutral-50'>What a sprint is not.</h2>
        <p className='mt-6 border-l-2 border-primary-light pl-4 font-heading text-2xl leading-snug text-neutral-50'>
          If your problem can't reasonably be solved in a sprint, we'll tell you.
        </p>
      </div>
      <ul className='grid grid-cols-1 gap-6 sm:grid-cols-2 lg:col-span-7'>
        {NOT_A_SPRINT.map(({ title, body }) => (
          <li key={title} className='rounded-lg bg-neutral-900 p-6'>
            <div className='flex items-start gap-3'>
              {/* The style guide's dark-surface error red, on a 10% tint of itself. */}
              <span className='flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#F87171]/10'>
                <FontAwesomeIcon icon={faXmark} className='text-[#F87171]' size='sm' />
              </span>
              <div>
                <h3 className='font-heading text-xl text-neutral-50'>{title}</h3>
                <p className='mt-2 font-body text-sm leading-relaxed text-neutral-400'>{body}</p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};


const CaseStudy = () => {
  return (
    <section id='case-study' className='scroll-mt-20 border-t border-neutral-800 py-16'>
      <div className='grid grid-cols-1 gap-12 lg:grid-cols-12'>
        <div className='lg:col-span-5'>
          <Overline>Case study · Agentic AI in production</Overline>
          <h2 className='font-heading text-4xl tracking-tight text-neutral-50'>Agents that ship, not demos that stall.</h2>
          <p className='mt-4 font-body text-neutral-400'>
            Most AI initiatives die between the prototype and production. These didn't. They run against real
            customer data, real business systems, and real money, and they're the experience behind the AI
            Automation Sprint.
          </p>
          <ul className='mt-8 flex flex-wrap gap-2'>
            {AI_STACK.map((tool) => (
              <li key={tool} className='rounded-full border border-neutral-800 bg-neutral-900 px-3 py-1 font-mono text-xs text-neutral-300'>
                {tool}
              </li>
            ))}
          </ul>
        </div>
        <ul className='flex flex-col gap-4 lg:col-span-7'>
          {CASE_STUDY.map((item) => (
            <li key={item} className='flex items-start gap-4 rounded-lg border-l-2 border-primary-light bg-neutral-900 p-5 font-body text-neutral-300'>
              <FontAwesomeIcon icon={faArrowRight} className='mt-1.5 flex-shrink-0 text-primary' size='xs' />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};


// The fallback for work no sprint fits, so it sits below the whole catalog
// and right after "If your problem can't reasonably be solved in a sprint".
const Bespoke = () => {
  return (
    <section id='bespoke' className='scroll-mt-20 border-t border-neutral-800 py-16'>
      <div className='grid grid-cols-1 gap-10 rounded-lg border border-neutral-800 bg-neutral-900 p-6 sm:p-10 lg:grid-cols-12'>
        <div className='lg:col-span-7'>
          <Overline>When no sprint fits</Overline>
          <h2 className='font-heading text-4xl tracking-tight text-neutral-50'>Bespoke engineering.</h2>
          <p className='mt-4 max-w-[60ch] font-body text-lg text-neutral-400'>
            Some work doesn't fit a fixed scope, a fixed timeline, and a fixed price. For that, we work as a
            traditional contract, billed by the hour, with the same engineering standards as every sprint.
          </p>
          <h3 className='mt-8 font-body text-xs uppercase tracking-widest text-neutral-500'>Good for</h3>
          <CheckList items={BESPOKE_FITS} className='mt-4' />
        </div>
        <div className='flex flex-col justify-center gap-6 lg:col-span-5'>
          <div className='rounded-lg border border-neutral-700 bg-neutral-950 p-6'>
            <p className='font-body text-xs uppercase tracking-widest text-neutral-500'>Hourly rate</p>
            <p className='mt-3'>
              <span className='font-mono text-3xl text-neutral-50'>{formatUsd(BESPOKE_RATE)}</span>
              <span className='font-body text-sm text-neutral-500'> / hour</span>
            </p>
          </div>
          <OutlineButton href={contactHref('Bespoke Engineering')}>Discuss a Bespoke Project</OutlineButton>
        </div>
      </div>
    </section>
  );
};


// Sprints build a defined thing; leading the engineering org is the other
// service. One pointer is enough to send the right buyer there, but only
// while that page is live (SHOW_FRACTIONAL_CTO_PAGE).
const CtoCallout = () => {
  const config = useConfig();
  if (!config?.showFractionalCtoPage) return null;

  return (
    <section className='border-t border-neutral-800 py-16'>
      <div className='flex flex-col gap-6 rounded-lg border-l-2 border-primary-light bg-neutral-900 p-6 sm:p-10 md:flex-row md:items-center md:justify-between'>
        <div>
          <h2 className='font-heading text-3xl tracking-tight text-neutral-50'>Need someone to lead the engineering organization?</h2>
          <p className='mt-2 max-w-[60ch] font-body text-neutral-400'>
            Sprints are for building a specific thing. If you need someone to decide what gets built, how it gets
            built, and how the team operates, consider a Fractional CTO instead.
          </p>
        </div>
        <Link
          to='/fractional-cto'
          className='inline-flex h-11 flex-shrink-0 items-center justify-center gap-2.5 rounded-lg border border-neutral-700 px-6 font-body text-[15px] tracking-wide text-primary-light transition-colors duration-150 hover:border-neutral-500 hover:text-primary-foreground'
        >
          Fractional CTO
          <FontAwesomeIcon icon={faArrowRight} size='xs' />
        </Link>
      </div>
    </section>
  );
};


const ClosingCta = () => {
  return (
    <section className='mb-16 rounded-lg border border-neutral-800 bg-neutral-900 px-6 py-12 text-center sm:px-12'>
      <h2 className='font-heading text-4xl tracking-tight text-neutral-50'>Have something you need built?</h2>
      <p className='mx-auto mt-4 max-w-[60ch] font-body text-neutral-400'>
        Tell us what you're trying to accomplish. We'll determine whether it fits an existing sprint, needs a
        custom engagement, or requires a different approach entirely.
      </p>
      <div className='mt-8'>
        <PrimaryButton href={contactHref('Engineering Sprint')}>Start a Conversation</PrimaryButton>
      </div>
      <p className='mt-6 flex flex-wrap items-center justify-center gap-2 font-body text-sm text-neutral-400'>
        <FontAwesomeIcon icon={faEnvelope} className='text-primary-light' />
        No mail app? Write to
        <a href={contactHref('Engineering Sprint')} className='text-primary-light underline hover:text-primary-foreground'>{CONTACT_EMAIL}</a>
      </p>
    </section>
  );
};


const EngineeringSprintsPage = () => {
  // The router doesn't scroll to a #hash on client-side navigation, and the
  // Fractional CTO page links straight to #case-study.
  const { hash } = useLocation();
  useEffect(() => {
    const target = hash && document.getElementById(hash.slice(1));
    if (target) {
      target.scrollIntoView();
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
      <Hero />
      <HowItWorks />
      <Catalog />
      <CaseStudy />
      <Included />
      <NotASprint />
      <Bespoke />
      <CtoCallout />
      <AdHocAdvice className='mb-8' />
      <ClosingCta />
    </div>
  );
};

export default EngineeringSprintsPage;
